/** BlockNote editor wrapper: loads content from API, auto-saves on change (debounced 2s, deferred markdown) */

import { useEffect, useRef, useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useCreateBlockNote } from '@blocknote/react'
import { BlockNoteView } from '@blocknote/mantine'
import {
  SuggestionMenuController,
  getDefaultReactSlashMenuItems,
  FormattingToolbarController,
  FormattingToolbar,
  BlockTypeSelect,
  BasicTextStyleButton,
  TextAlignButton,
  ColorStyleButton,
  NestBlockButton,
  UnnestBlockButton,
  CreateLinkButton,
} from '@blocknote/react'
import { BlockNoteSchema, defaultBlockSpecs, filterSuggestionItems } from '@blocknote/core'
import '@blocknote/mantine/style.css'
import { useDocument, useUpdateDocument } from '../../hooks/use-documents'
import { useAppStore } from '../../stores/app-store'
import { useAI } from '../../hooks/use-ai'
import { getAISlashMenuItems } from './ai-slash-commands'
import { AISelectionToolbar } from './ai-selection-toolbar'
import { cn } from '../../lib/utils'
import { API_BASE } from '../../lib/api-client'
import { sanitizeCodeFences } from '../../lib/sanitize-markdown-code-fences'
import { shouldUseMarkdownFallback } from '../../lib/editor-content-fallback'
import { appendUploadDescription } from '../../lib/upload-description'
import { createPasteMarkdownPlugin, pasteMarkdownPluginKey } from './paste-markdown-extension'
import { Blockquote } from './blockquote-block'
import { DocumentProvenanceBanner } from './document-provenance-banner'
import { DocumentTableOfContents } from './document-table-of-contents'
import { extractDocumentHeadings } from '../../lib/document-reader'
import { getWebFeatureProfile } from '../../lib/feature-flags'
import { documentEditPath, documentReadPath } from '../../lib/document-route'
import { createSerialTaskQueue, persistDocumentBeforeRead } from '../../lib/document-edit-finish'

/** Editor schema with blockquote support */
const schema = BlockNoteSchema.create({
  blockSpecs: { ...defaultBlockSpecs, blockquote: Blockquote },
})

// Safari lacks requestIdleCallback — polyfill with setTimeout (module-level, evaluated once)
const rIC: typeof requestIdleCallback =
  typeof window !== 'undefined' && window.requestIdleCallback
    ? window.requestIdleCallback.bind(window)
    : (cb) =>
        window.setTimeout(
          () => cb({ didTimeout: false, timeRemaining: () => 50 } as IdleDeadline),
          0,
        )
const cIC: typeof cancelIdleCallback =
  typeof window !== 'undefined' && window.cancelIdleCallback
    ? window.cancelIdleCallback.bind(window)
    : clearTimeout

interface EditorProps {
  documentId: string
  tabId: string
}

export function Editor({ documentId, tabId }: EditorProps) {
  const storageEnabled = getWebFeatureProfile().storageSpace
  const { data: doc, isLoading } = useDocument(documentId)
  const updateDocument = useUpdateDocument()
  const { markTabDirty, updateTabTitle, theme } = useAppStore()
  const { isGenerating, error: aiError, generate, transform } = useAI()
  const navigate = useNavigate()
  const location = useLocation()
  const [isFinishing, setIsFinishing] = useState(false)
  const headings = useMemo(
    () => (doc ? extractDocumentHeadings(doc.content, doc.contentJson) : []),
    [doc],
  )
  const hasArticleHeading = headings.some((heading) => heading.level === 1)

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const markdownIdleRef = useRef<number | null>(null)
  const isDirtyRef = useRef(false)
  const mountedRef = useRef(true)
  const initializedRef = useRef(false)
  const saveRevisionRef = useRef(0)
  const enqueueSaveRef = useRef(createSerialTaskQueue())
  const titleDraftRef = useRef<string | null>(null)

  if (doc && titleDraftRef.current === null) titleDraftRef.current = doc.title

  const editor = useCreateBlockNote({
    schema,
    uploadFile: storageEnabled
      ? async (file: File) => {
          if (!file.type.startsWith('image/')) {
            throw new Error('Only image files are supported')
          }

          const formData = new FormData()
          formData.append('file', file)
          appendUploadDescription(formData, file, 'editor-image')
          const res = await fetch(`${API_BASE}/api/uploads`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
          })
          if (!res.ok) {
            const err = await res.json().catch(() => null)
            throw new Error(err?.error ?? `Upload failed (${res.status})`)
          }

          const data = (await res.json()) as { fileKey?: string }
          if (!data.fileKey) throw new Error('Upload response missing fileKey')
          return `${API_BASE}/api/files/${data.fileKey}`
        }
      : undefined,
  })

  // Register paste-markdown plugin once doc is loaded AND TipTap's EditorView exists.
  // BlockNoteView creates the EditorView in its own effect; if it hasn't run yet,
  // we defer via tiptap.on('create') to avoid "Cannot read properties of undefined".
  useEffect(() => {
    if (!doc) return

    const tiptap = editor._tiptapEditor
    if (!tiptap) return
    let registered = false
    let cleaned = false

    const tryRegister = () => {
      if (registered || cleaned || tiptap.isDestroyed || !tiptap.view) return
      try {
        const plugin = createPasteMarkdownPlugin(editor)
        tiptap.registerPlugin(plugin, (p, existing) => [p, ...existing])
        registered = true
      } catch (err) {
        console.warn('paste-markdown plugin registration failed:', err)
      }
    }

    // Attempt immediately — view may already exist from a previous mount
    tryRegister()
    // Fallback: if view wasn't ready, wait for TipTap's 'create' event
    if (!registered) tiptap.on('create', tryRegister)

    return () => {
      cleaned = true
      tiptap.off('create', tryRegister)
      if (registered && !tiptap.isDestroyed) {
        try {
          tiptap.unregisterPlugin(pasteMarkdownPluginKey)
        } catch {
          /* view already gone */
        }
      }
    }
  }, [editor, doc])

  // Load initial content once doc is fetched
  useEffect(() => {
    if (!doc || initializedRef.current) return
    initializedRef.current = true

    const loadContent = async () => {
      if (doc.contentJson && !shouldUseMarkdownFallback(doc.contentJson, doc.content)) {
        try {
          const blocks = doc.contentJson as Parameters<typeof editor.replaceBlocks>[1]
          editor.replaceBlocks(editor.document, blocks)
          return
        } catch {
          // Fall through to markdown parsing
        }
      }
      // Fallback: parse markdown content (e.g. imported .md files have content but no contentJson)
      if (doc.content) {
        try {
          // Sanitize bare code fences (``` without language) — BlockNote crashes on them
          const safeMd = sanitizeCodeFences(doc.content)
          const blocks = await editor.tryParseMarkdownToBlocks(safeMd)
          editor.replaceBlocks(editor.document, blocks)
        } catch (err) {
          console.error('Markdown→blocks parse failed:', err)
        }
      }
    }
    loadContent()
  }, [doc, editor])

  // Debounced auto-save: contentJson first (fast), markdown deferred via requestIdleCallback
  const handleChange = useCallback(() => {
    const revision = ++saveRevisionRef.current
    // Skip redundant dirty-state updates
    if (!isDirtyRef.current) {
      isDirtyRef.current = true
      markTabDirty(tabId, true)
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      try {
        const contentJson = editor.document

        // Stage 1: save contentJson immediately (near-zero cost)
        await enqueueSaveRef.current(() =>
          updateDocument.mutateAsync({
            id: documentId,
            contentJson,
          }),
        )

        if (revision !== saveRevisionRef.current) return

        isDirtyRef.current = false
        markTabDirty(tabId, false)

        // Stage 2: defer expensive markdown conversion until browser is idle
        // Capture snapshot NOW (not at idle time) to avoid stale data
        const snapshotBlocks = editor.document
        if (markdownIdleRef.current) cIC(markdownIdleRef.current)
        markdownIdleRef.current = rIC(
          async () => {
            if (!mountedRef.current) return // guard against unmounted async work
            try {
              const content = await editor.blocksToMarkdownLossy(snapshotBlocks)
              if (!mountedRef.current || revision !== saveRevisionRef.current) return
              await enqueueSaveRef.current(() =>
                updateDocument.mutateAsync({ id: documentId, content }),
              )
            } catch (err) {
              console.error('Deferred markdown sync failed:', err)
            }
          },
          { timeout: 5000 },
        )
      } catch (err) {
        console.error('Auto-save failed:', err)
      }
    }, 2000)
  }, [editor, documentId, tabId, markTabDirty, updateDocument])

  const handleDone = useCallback(async () => {
    if (!doc || isFinishing) return
    setIsFinishing(true)
    saveRevisionRef.current += 1
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    if (markdownIdleRef.current) cIC(markdownIdleRef.current)

    try {
      const contentJson = editor.document
      const savedDocument = await persistDocumentBeforeRead({
        documentId,
        snapshot: contentJson,
        title: titleDraftRef.current ?? doc.title,
        persistedTitle: doc.title,
        toMarkdown: (snapshot) => editor.blocksToMarkdownLossy(snapshot),
        save: (document) => enqueueSaveRef.current(() => updateDocument.mutateAsync(document)),
      })
      isDirtyRef.current = false
      markTabDirty(tabId, false)
      navigate(documentReadPath(savedDocument), { state: location.state })
    } catch (err) {
      console.error('Failed to finish editing:', err)
      setIsFinishing(false)
    }
  }, [
    doc,
    documentId,
    editor,
    isFinishing,
    location.state,
    markTabDirty,
    navigate,
    tabId,
    updateDocument,
  ])

  const saveTitle = useCallback(
    async (newTitle: string) => {
      if (!doc || newTitle === doc.title) return doc
      titleDraftRef.current = newTitle
      updateTabTitle(tabId, newTitle)
      const updated = await enqueueSaveRef.current(() =>
        updateDocument.mutateAsync({ id: documentId, title: newTitle }),
      )
      if (updated.slug && updated.slug !== doc.slug) {
        navigate(documentEditPath(updated), { replace: true, state: location.state })
      }
      return updated
    },
    [doc, documentId, location.state, navigate, tabId, updateDocument, updateTabTitle],
  )

  // Update tab title when doc title is changed externally
  useEffect(() => {
    if (doc?.title) {
      updateTabTitle(tabId, doc.title)
    }
  }, [doc?.title, tabId, updateTabTitle])

  // Cleanup timers on unmount + guard async work
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      if (markdownIdleRef.current) cIC(markdownIdleRef.current)
    }
  }, [])

  if (isLoading) {
    return (
      <div
        className={cn(
          'flex h-full items-center justify-center',
          theme === 'dark' ? 'bg-neutral-950' : 'bg-white',
        )}
      >
        <div className="space-y-3 w-full max-w-2xl px-8">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-4 animate-pulse rounded',
                theme === 'dark' ? 'bg-neutral-800' : 'bg-neutral-100',
              )}
              style={{ width: `${60 + i * 8}%` }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (!doc) {
    return (
      <div
        className={cn(
          'flex h-full items-center justify-center',
          theme === 'dark' ? 'bg-neutral-950 text-neutral-400' : 'bg-white text-neutral-500',
        )}
      >
        <p className="text-sm">Document not found.</p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'aw-doc-editor flex h-full flex-col',
        doc.sourceProvenance && hasArticleHeading && 'has-article-heading',
        theme === 'dark' ? 'bg-neutral-950' : 'bg-white',
      )}
    >
      {/* AI error indicator */}
      {aiError && (
        <div className="mx-4 mb-2 rounded bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950 dark:text-red-400">
          AI error: {aiError}
        </div>
      )}

      {/* BlockNote editor with AI slash commands and selection toolbar */}
      <div className="aw-doc-body flex-1 overflow-y-auto" aria-label="Document content">
        <article className="aw-doc-body-inner">
          <div className="aw-doc-readable-column">
            <header className="aw-doc-editor-head">
              <h1 className="sr-only">Edit {doc.title}</h1>
              <div className="aw-editor-reader-row">
                <button
                  type="button"
                  className="aw-editor-reader-link"
                  onClick={() => void handleDone()}
                  disabled={isFinishing}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  {isFinishing ? 'Saving…' : 'Done editing'}
                </button>
              </div>
              <DocumentProvenanceBanner doc={doc} />
              <textarea
                rows={1}
                maxLength={500}
                aria-label="Document title"
                defaultValue={doc.title}
                placeholder="Untitled"
                className={cn(
                  'aw-doc-title-input w-full bg-transparent text-2xl font-bold outline-none placeholder-neutral-500 md:text-3xl',
                  theme === 'dark' ? 'text-neutral-100' : 'text-neutral-900',
                )}
                onChange={(event) => {
                  titleDraftRef.current = event.currentTarget.value
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const newTitle = e.currentTarget.value.trim() || 'Untitled'
                    if (newTitle !== doc.title) {
                      void saveTitle(newTitle).catch((err) =>
                        console.error('Failed to update title:', err),
                      )
                    }
                    editor.focus()
                  }
                }}
                onBlur={async (e) => {
                  const newTitle = e.target.value.trim() || 'Untitled'
                  if (newTitle === doc.title) return
                  try {
                    await saveTitle(newTitle)
                  } catch (err) {
                    console.error('Failed to update title:', err)
                  }
                }}
              />
            </header>

            <DocumentTableOfContents headings={headings} className="aw-doc-toc-mobile" />

            <section className="aw-doc-reader-surface" aria-label="Editable knowledge content">
              <BlockNoteView
                editor={editor}
                onChange={handleChange}
                theme={theme}
                className="aw-doc-blocknote min-h-full"
                slashMenu={false}
                formattingToolbar={false}
              >
                <SuggestionMenuController
                  triggerCharacter="/"
                  getItems={async (query) =>
                    filterSuggestionItems(
                      [
                        ...getDefaultReactSlashMenuItems(editor),
                        {
                          title: 'Blockquote',
                          subtext: 'Insert a quote block',
                          group: 'Basic blocks',
                          aliases: ['quote', 'blockquote', '>'],
                          onItemClick: () => {
                            editor.insertBlocks(
                              [{ type: 'blockquote' as never }],
                              editor.getTextCursorPosition().block,
                              'after',
                            )
                          },
                        },
                        ...getAISlashMenuItems(editor, documentId, generate),
                      ],
                      query,
                    )
                  }
                />
                <FormattingToolbarController
                  formattingToolbar={() => (
                    <FormattingToolbar>
                      <BlockTypeSelect key="blockTypeSelect" />
                      <BasicTextStyleButton basicTextStyle="bold" key="boldStyleButton" />
                      <BasicTextStyleButton basicTextStyle="italic" key="italicStyleButton" />
                      <BasicTextStyleButton basicTextStyle="underline" key="underlineStyleButton" />
                      <BasicTextStyleButton basicTextStyle="strike" key="strikeStyleButton" />
                      <BasicTextStyleButton basicTextStyle="code" key="codeStyleButton" />
                      <TextAlignButton textAlignment="left" key="textAlignLeftButton" />
                      <TextAlignButton textAlignment="center" key="textAlignCenterButton" />
                      <TextAlignButton textAlignment="right" key="textAlignRightButton" />
                      <ColorStyleButton key="colorStyleButton" />
                      <NestBlockButton key="nestBlockButton" />
                      <UnnestBlockButton key="unnestBlockButton" />
                      <CreateLinkButton key="createLinkButton" />
                      <AISelectionToolbar
                        key="aiToolbar"
                        editor={editor}
                        documentId={documentId}
                        transform={transform}
                        isGenerating={isGenerating}
                      />
                    </FormattingToolbar>
                  )}
                />
              </BlockNoteView>
            </section>
          </div>

          <DocumentTableOfContents headings={headings} className="aw-doc-toc-desktop" />
        </article>
      </div>
    </div>
  )
}
